import React from 'react';
import PredictionGridChemicalPhysical from 'components/molecules/PredictionGridChemicalPhysical';
import { useSelectFilter } from 'hooks/planning/useFilter';
import { IData, IRow } from 'components/molecules/PredictionGrid/types';
import {
  postGridChemicalPhysical,
  postExportChemicalPhysicalGrid,
} from 'services/prediction/grid-chemical-physical';
import { useQuery } from 'react-query';
import qs from 'querystring';
import { openInNewTab } from 'helpers';
import { CHEMICAL_PHYSICAL_GRID_VALIDATION_RULES } from 'constants/grid/validationRules';
import { columns } from './data';

const TAKE = 10;
const MAX_ROWS = 100000;

const mapRows = result => {
  return result.map(row => {
    return {
      id: row.id,
      collection: row.collection,
      serieNumber: row.serieNumber,
      tag: row.tag,
      calota: row.calota,
      neutralization: row.neutralization,
      color: row.color,
      relativeDensity: row.relativeDensity,
      visualAspect: row.visualAspect,
      waterContentMeasured: row.waterContentMeasured,
      waterContentadjusted: row.waterContentadjusted,
      interfacialTension: row.interfacialTension,
      interfacialTensionMnm: row.interfacialTensionMnm,
      lossFactor: row.lossFactor,
      powerFactor: row.powerFactor,
      classification: row.classification,
      omegaClassification: row.omegaClassification,
      recommendation: row.recommendation,
      omegaRecommendation: row.omegaRecommendation,
    };
  });
};

const PredictionGridChemicalPhysicalContainer = () => {
  // INÍCIO ESTADO DOS FILTROS
  const [gridRows, setGridRows] = React.useState<IRow[]>([]);
  const [exportingGridData, setExportingGridData] =
    React.useState<boolean>(false);
  const [
    installationFilterOptionsContext,
    serialNumberFilterOptionsContext,
    selectedInitialDateOptionContext,
    selectedFinalDateOptionContext,
  ] = [
    useSelectFilter('installation'),
    useSelectFilter('serial-number'),
    useSelectFilter('initialDate'),
    useSelectFilter('finalDate'),
  ];

  const allFilterContexts = React.useMemo(
    () => [
      serialNumberFilterOptionsContext,
      installationFilterOptionsContext,
      selectedInitialDateOptionContext,
      selectedFinalDateOptionContext,
    ],
    [
      serialNumberFilterOptionsContext,
      installationFilterOptionsContext,
      selectedInitialDateOptionContext,
      selectedFinalDateOptionContext,
    ],
  );

  // INÍCIO ESTADO DAS CONFIGURAÇÕES DAS GRID

  const [dataGrid, setDataGrid] = React.useState(() => columns);

  // FIM ESTADO DAS CONFIGURAÇÕES DAS GRID

  // INÍCIO ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID
  const [totalRows, setTotalRows] = React.useState(MAX_ROWS);
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(TAKE);
  const pageSizes = React.useMemo(() => [10, 20, 30, 40, 50, 100], []);
  // FIM ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID

  const installationIds = installationFilterOptionsContext.map(item =>
    String(item.value),
  );

  const serieNumber = serialNumberFilterOptionsContext.map(item =>
    String(item.value),
  );

  const startDateMap = selectedInitialDateOptionContext.map(item =>
    String(item.value),
  );

  const start = React.useMemo(() => startDateMap[0], [startDateMap]);

  const endDateMap = selectedFinalDateOptionContext.map(item =>
    String(item.value),
  );

  const end = React.useMemo(() => endDateMap[0], [endDateMap]);
  const gridOrder = React.useMemo(() => {
    let orderResult = dataGrid
      .filter(column => column.direction)
      .map(column => ({
        columnName: column.name,
        order: column?.direction?.toLocaleUpperCase(),
      }));

    const orderCollection = orderResult.findIndex(
      item => item.columnName === 'collection',
    );

    if (orderCollection >= 0) {
      const [collection] = orderResult.splice(orderCollection, 1);

      if (collection) {
        const newColComments = [
          { columnName: 'sampleDate', order: collection.order },
        ];

        orderResult = [...orderResult, ...newColComments];
      }
    }
    return orderResult;
  }, [dataGrid]);

  // AO ALTERAR OS FILTROS, DEVERÁ RETORNAR PARA PÁGINA INICIAL
  React.useEffect(() => {
    setCurrentPage(0);
  }, [allFilterContexts, pageSize]);

  const queryPredictionChemicalPhysicalGrid = useQuery({
    queryKey: [
      'PredictionChemicalPhysicalContext',
      [pageSize, currentPage, gridOrder, ...allFilterContexts],
    ],
    queryFn: async () => {
      const qsparams = qs.stringify({
        page: currentPage + 1,
        take: pageSize,
      });
      const params = {
        order: gridOrder,
        installationIds,
        serieNumber,
        start,
        end,
      };

      const { data } = await postGridChemicalPhysical(qsparams, params);
      return data;
    },
    select: data => {
      const rows: IRow[] = mapRows(data.result);
      return {
        count: data.count,
        rows,
      };
    },
    onSuccess: ({ count, rows }: { rows: IRow[]; count: number }) => {
      setGridRows(rows);
      setTotalRows(count);
    },
    enabled: true,
    cacheTime: 0,
  });

  const exportGridData = React.useCallback(() => {
    setExportingGridData(true);

    const qsparams = qs.stringify({
      page: currentPage + 1,
      take: pageSize,
    });

    const params = {
      order: gridOrder,
      installationIds,
      serieNumber,
      start,
      end,
    };

    postExportChemicalPhysicalGrid(qsparams, params)
      .then(({ url }) => {
        openInNewTab(url);
      })
      .finally(() => setExportingGridData(false));
  }, [
    currentPage,
    serieNumber,
    start,
    end,
    gridOrder,
    installationIds,
    pageSize,
  ]);

  const getRowId = React.useCallback((row: IRow) => String(row.id), []);

  const getRowById = React.useCallback(
    id => {
      const strId = `${id}`;
      return gridRows.find(row => `${row?.id}` === strId);
    },
    [gridRows],
  );
  const onSortingChange = React.useCallback(sortedList => {
    setDataGrid(oldDataGrid => {
      const data = oldDataGrid.map(item => {
        const foundItem = sortedList.find(
          sortItem => sortItem.columnName === item.name,
        );
        if (foundItem) {
          return {
            ...item,
            direction: foundItem.direction,
          };
        }
        const newItem = { ...item };
        delete newItem.direction;
        return newItem;
      });
      return data;
    });
  }, []);

  const refetch = React.useCallback(() => {
    queryPredictionChemicalPhysicalGrid.refetch();
  }, [queryPredictionChemicalPhysicalGrid]);
  return (
    <PredictionGridChemicalPhysical
      columns={dataGrid as IData[]}
      rows={gridRows}
      loading={
        queryPredictionChemicalPhysicalGrid?.isLoading || exportingGridData
      }
      dayMonthYearCells={['collection']}
      tooltipCells={[
        'calota',
        'neutralization',
        'relativeDensity',
        'waterContentMeasured',
        'waterContentadjusted',
        'interfacialTension',
        'interfacialTensionMnm',
        'lossFactor',
        'powerFactor',
      ]}
      tooltipRecommendationCells={['recommendation']}
      tooltipMaxStrLength={5}
      validationRules={CHEMICAL_PHYSICAL_GRID_VALIDATION_RULES}
      onSortingChange={onSortingChange}
      getRowId={getRowId}
      currentPage={currentPage}
      pageSize={pageSize}
      pageSizes={pageSizes}
      totalCount={totalRows}
      onCurrentPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      getRowById={getRowById}
      onExport={exportGridData}
      refetch={refetch}
    />
  );
};

export default PredictionGridChemicalPhysicalContainer;
